'use client';

import React, { useState } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import { MessageCircle, ExternalLink } from 'lucide-react';
import { Toaster } from 'sonner';
import ClubMuseumTab from './tabs/ClubMuseumTab';
import SmileMatineeTab from './tabs/SmileMatineeTab';
import AdnOficialTab from './tabs/AdnOficialTab';
import MuseumMdqTab from './tabs/MuseumMdqTab';
import ListaFreeModal from './ListaFreeModal';
import type { EventData, ModalConfig } from '../types/events';

const TABS = [
  { id: 'museum', label: 'Club Museum', emoji: '🏛️' },
  { id: 'smile', label: 'Smile Matinée', emoji: '😃' },
  { id: 'adn', label: 'ADN Oficial', emoji: '🧬' },
  { id: 'mdq', label: 'Museum MDQ', emoji: '🌊' },
] as const;

type TabId = typeof TABS[number]['id'];

const WA_NUMBER = '5492236043343';

export function buildWhatsAppUrl(message: string): string {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`;
}

export default function CarteleraClient() {
  const [activeTab, setActiveTab] = useState<TabId>('museum');
  const [modalConfig, setModalConfig] = useState<ModalConfig | null>(null);

  const openListaFree = (event: EventData) => {
    setModalConfig({ event });
  };

  const closeModal = () => setModalConfig(null);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: 'var(--card)',
            color: 'var(--foreground)',
            border: '1px solid var(--border)',
          },
        }}
      />

      {/* Header — Title + WhatsApp CTA */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16">
          <div className="flex items-center justify-between h-16">
            {/* Logo + Title */}
            <div className="flex items-center gap-3">
              <AppLogo size={36} />
              <div className="flex flex-col">
                <span className="font-extrabold text-xl tracking-tight bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Olivera RRPP
                </span>
                {/* PR Badge */}
                <span
                  className="text-[9px] sm:text-[10px] font-black tracking-[0.15em] uppercase leading-none px-2 py-0.5 rounded-full"
                  style={{
                    color: '#ff00cc',
                    border: '1px solid #ff00cc',
                    boxShadow: '0 0 8px rgba(255,0,204,0.7), 0 0 16px rgba(255,0,204,0.3), inset 0 0 6px rgba(255,0,204,0.1)',
                    textShadow: '0 0 8px #ff00cc, 0 0 16px rgba(255,0,204,0.6)',
                    background: 'rgba(255,0,204,0.08)',
                  }}
                >
                  Relaciones Públicas // Olivera
                </span>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <a
              href={buildWhatsAppUrl('Hola Benja! Quiero atención personalizada 🙌')}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp flex items-center gap-2 px-4 py-2 rounded-full text-white font-semibold text-sm transition-all active:scale-95"
            >
              <MessageCircle size={16} />
              <span className="hidden sm:inline">ATENCIÓN PERSONALIZADA</span>
              <span className="sm:hidden">WhatsApp</span>
            </a>
          </div>
        </div>
      </header>

      {/* Venue Tabs — Desktop */}
      <div className="sticky top-16 z-30 bg-background/95 backdrop-blur-md border-b border-border hidden sm:block">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16">
          <div className="flex gap-1">
            {TABS.map((tab) => (
              <button
                key={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold transition-all duration-200 ${
                  activeTab === tab.id ? 'tab-active' : 'tab-inactive hover:text-foreground/80'
                }`}
              >
                <span>{tab.emoji}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 max-w-screen-2xl mx-auto w-full px-4 lg:px-8 xl:px-10 2xl:px-16 py-8 pb-28 sm:pb-8">
        {activeTab === 'museum' && (
          <ClubMuseumTab openListaFree={openListaFree} />
        )}
        {activeTab === 'smile' && (
          <SmileMatineeTab openListaFree={openListaFree} />
        )}
        {activeTab === 'adn' && (
          <AdnOficialTab openListaFree={openListaFree} />
        )}
        {activeTab === 'mdq' && (
          <MuseumMdqTab openListaFree={openListaFree} />
        )}
      </main>

      {/* Mobile Bottom Tab Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-md border-t border-border sm:hidden">
        <div className="grid grid-cols-4 h-16">
          {TABS.map((tab) => (
            <button
              key={`mobile-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center justify-center gap-1 transition-all duration-200 ${
                activeTab === tab.id ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              <span className="text-lg leading-none">{tab.emoji}</span>
              <span className="text-[10px] font-semibold leading-none truncate px-1">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="hidden sm:block border-t border-border bg-card/50 py-6">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <AppLogo size={28} />
              <span className="font-bold text-sm text-muted-foreground">Olivera RRPP</span>
            </div>
            <div className="flex items-center gap-6">
              <a
                href="https://instagram.com/23.beenn"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm font-medium"
              >
                <ExternalLink size={16} />
                @23.beenn
              </a>
              <a
                href={buildWhatsAppUrl('Hola Benja!')}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-muted-foreground hover:text-green-400 transition-colors text-sm font-medium"
              >
                <MessageCircle size={16} />
                +54 9 223-604-3343
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Lista Free Modal */}
      {modalConfig && (
        <ListaFreeModal config={modalConfig} onClose={closeModal} />
      )}
    </div>
  );
}